import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { BarChart3, FileText, MessageSquare, LogOut, Eye, Download, ChevronLeft, ChevronRight, X, Calendar, User, Settings, Send, UserPlus, Lock, Trash2, FileSpreadsheet } from 'lucide-react';
import { getResponses, getSessionResponses, exportData, exportExcel, deleteSession, API_BASE_URL } from '../api';
import toast from 'react-hot-toast';

const AdminResponses = () => {
  const navigate = useNavigate();
  const [sessions, setSessions] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionDetail, setSessionDetail] = useState(null);
  const [filter, setFilter] = useState('all'); // all | agent | citizen

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) { navigate('/admin'); return; }
    const user = JSON.parse(localStorage.getItem('admin_user') || '{}');
    if (user.role === 'agent') { navigate('/agent'); return; }
    loadResponses(1);
  }, [navigate]);

  const loadResponses = async (page) => {
    try {
      const res = await getResponses(page);
      setSessions(res.data.sessions);
      setPagination(res.data.pagination);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/admin'); }
    }
    setLoading(false);
  };

  const viewSession = async (sessionId) => {
    try {
      const res = await getSessionResponses(sessionId);
      setSessionDetail(res.data);
      setSelectedSession(sessionId);
    } catch (err) {
      toast.error('Erreur de chargement');
    }
  };

  const handleExport = async () => {
    try {
      const res = await exportData();
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `enquete_khombole_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export reussi');
    } catch (err) {
      toast.error('Erreur lors de l\'export');
    }
  };

  const handleExportExcel = async () => {
    try {
      const res = await exportExcel();
      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `enquete_khombole_${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export Excel reussi');
    } catch (err) {
      toast.error('Erreur lors de l\'export Excel');
    }
  };

  const handleDelete = async (sessionId) => {
    if (!window.confirm('Supprimer définitivement cette enquête ?')) return;
    try {
      await deleteSession(sessionId);
      toast.success('Enquête supprimée');
      loadResponses(pagination.page);
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleLogout = () => { localStorage.clear(); navigate('/admin'); };

  return (
    <div style={{ minHeight: '100vh', background: '#f5f7f5' }}>
      {/* Sidebar */}
      <div style={{ position: 'fixed', left: 0, top: 0, bottom: 0, width: '240px', background: 'linear-gradient(180deg, #1B5E20, #2E7D32)', padding: '24px 16px', display: 'flex', flexDirection: 'column', zIndex: 100 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '40px', padding: '0 8px' }}>
          <img src="/logo.webp" alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', background: 'white', padding: '2px' }} />
          <div>
            <div style={{ color: '#FFD54F', fontSize: '10px', fontWeight: '600', letterSpacing: '0.5px' }}>ADMIN</div>
            <div style={{ color: 'white', fontSize: '14px', fontWeight: '600' }}>Khombole</div>
          </div>
        </div>
        <nav style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {[
            { icon: <BarChart3 size={18} />, label: 'Tableau de bord', path: '/admin/dashboard' },
            { icon: <FileText size={18} />, label: 'Questions', path: '/admin/questions' },
            { icon: <MessageSquare size={18} />, label: 'Reponses', path: '/admin/responses', active: true },
            { icon: <UserPlus size={18} />, label: 'Agents', path: '/admin/agents' },
            { icon: <Settings size={18} />, label: 'Paramètres', path: '/admin/settings' },
            { icon: <Lock size={18} />, label: 'Mon Compte', path: '/admin/profile' },
          ].map((item, i) => (
            <button key={i} onClick={() => navigate(item.path)}
              style={{ background: item.active ? 'rgba(255,255,255,0.15)' : 'transparent', border: 'none', color: 'white', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px', fontWeight: item.active ? '600' : '400', textAlign: 'left', width: '100%' }}>
              {item.icon} {item.label}
            </button>
          ))}
        </nav>
        <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.08)', border: 'none', color: 'rgba(255,255,255,0.7)', padding: '12px 16px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '13px' }}>
          <LogOut size={18} /> Deconnexion
        </button>
      </div>

      {/* Main Content */}
      <div style={{ marginLeft: '240px', padding: '32px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#1a1a1a', fontFamily: 'Playfair Display, serif' }}>Reponses Collectees</h1>
            <p style={{ color: '#888', fontSize: '13px', marginTop: '4px' }}>{pagination.total} sessions au total</p>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            <button onClick={handleExportExcel}
              style={{ background: 'linear-gradient(135deg, #1B5E20, #2E7D32)', color: 'white', border: 'none', padding: '12px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600' }}>
              <FileSpreadsheet size={16} /> Exporter (Excel)
            </button>
            <button onClick={handleExport}
              style={{ background: 'white', color: '#2E7D32', border: '1.5px solid #2E7D32', padding: '12px 20px', borderRadius: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '600' }}>
              <Download size={16} /> JSON
            </button>
          </div>
        </div>

        {/* Quick Stats + Filter */}
        <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap' }}>
          {[
            { key: 'all', label: 'Toutes', count: sessions.length, color: '#1B5E20', bg: '#E8F5E9' },
            { key: 'agent', label: 'Par Agents', count: sessions.filter(s => s.agent_id).length, color: '#1565C0', bg: '#E3F2FD' },
            { key: 'citizen', label: 'Par Citoyens', count: sessions.filter(s => !s.agent_id).length, color: '#E65100', bg: '#FFF3E0' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)}
              style={{
                background: filter === tab.key ? tab.bg : 'white',
                border: `1.5px solid ${filter === tab.key ? tab.color : '#e0e0e0'}`,
                color: filter === tab.key ? tab.color : '#666',
                padding: '10px 18px',
                borderRadius: '10px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: filter === tab.key ? '700' : '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                boxShadow: filter === tab.key ? `0 2px 8px ${tab.bg}` : 'none'
              }}>
              {tab.label} <span style={{ fontSize: '11px', fontWeight: '700', background: filter === tab.key ? 'white' : '#f5f5f5', padding: '2px 8px', borderRadius: '10px' }}>{tab.count}</span>
            </button>
          ))}
        </div>

        {/* Sessions Table */}
        <div style={{ background: 'white', borderRadius: '16px', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.04)' }}>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8faf8' }}>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666', borderBottom: '1px solid #eee' }}>Session ID</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666', borderBottom: '1px solid #eee' }}>Date</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666', borderBottom: '1px solid #eee' }}>Source</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666', borderBottom: '1px solid #eee' }}>Format</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666', borderBottom: '1px solid #eee' }}>Reponses</th>
                  <th style={{ padding: '14px 20px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#666', borderBottom: '1px solid #eee' }}>Statut</th>
                  <th style={{ padding: '14px 20px', textAlign: 'center', fontSize: '12px', fontWeight: '600', color: '#666', borderBottom: '1px solid #eee' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.filter(s => {
                  if (filter === 'agent') return !!s.agent_id;
                  if (filter === 'citizen') return !s.agent_id;
                  return true;
                }).map((session) => (
                  <tr key={session.id} style={{ borderBottom: '1px solid #f5f5f5' }}>
                    <td style={{ padding: '14px 20px', fontSize: '12px', color: '#555', fontFamily: 'monospace' }}>{session.id.substring(0, 8)}...</td>
                    <td style={{ padding: '14px 20px', fontSize: '13px', color: '#333' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Calendar size={12} color="#999" />
                        {new Date(session.started_at).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                      </div>
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      {session.agent_id ? (
                        <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '12px', background: '#E3F2FD', color: '#1565C0', fontWeight: '600', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                          <UserPlus size={12} /> Agent: {session.agent_name || session.agent_id}
                        </span>
                      ) : session.source === 'self_invite' ? (
                        <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '12px', background: '#FFF3E0', color: '#E65100', fontWeight: '600' }}>
                          Invitation
                        </span>
                      ) : (
                        <span style={{ fontSize: '11px', padding: '4px 10px', borderRadius: '12px', background: '#f5f5f5', color: '#666', fontWeight: '500' }}>
                          Direct
                        </span>
                      )}
                    </td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', background: session.response_format === 'audio' ? '#FFF8E1' : '#E8F5E9', color: session.response_format === 'audio' ? '#FF8F00' : '#2E7D32', fontWeight: '500' }}>
                        {session.response_format === 'audio' ? 'Audio' : 'Texte'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px', fontSize: '13px', color: '#333', fontWeight: '500' }}>{session.response_count}</td>
                    <td style={{ padding: '14px 20px' }}>
                      <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '12px', background: session.is_completed ? '#E8F5E9' : '#FFF3E0', color: session.is_completed ? '#2E7D32' : '#E65100', fontWeight: '500' }}>
                        {session.is_completed ? 'Terminee' : 'En cours'}
                      </span>
                    </td>
                    <td style={{ padding: '14px 20px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                        <button onClick={() => viewSession(session.id)}
                          style={{ background: '#f5f5f5', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#555' }}>
                          <Eye size={14} /> Voir
                        </button>
                        <button onClick={() => handleDelete(session.id)}
                          style={{ background: '#ffebee', border: 'none', borderRadius: '8px', padding: '8px 12px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#c62828' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderTop: '1px solid #eee' }}>
            <span style={{ fontSize: '12px', color: '#888' }}>Page {pagination.page} sur {pagination.pages}</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button onClick={() => loadResponses(pagination.page - 1)} disabled={pagination.page <= 1}
                style={{ background: '#f5f5f5', border: 'none', borderRadius: '6px', padding: '8px 12px', cursor: pagination.page <= 1 ? 'default' : 'pointer', opacity: pagination.page <= 1 ? 0.5 : 1 }}>
                <ChevronLeft size={14} />
              </button>
              <button onClick={() => loadResponses(pagination.page + 1)} disabled={pagination.page >= pagination.pages}
                style={{ background: '#f5f5f5', border: 'none', borderRadius: '6px', padding: '8px 12px', cursor: pagination.page >= pagination.pages ? 'default' : 'pointer', opacity: pagination.page >= pagination.pages ? 0.5 : 1 }}>
                <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

        {/* Session Detail Modal */}
        {selectedSession && sessionDetail && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '24px' }}>
            <div style={{ background: 'white', borderRadius: '20px', padding: '32px', width: '100%', maxWidth: '700px', maxHeight: '85vh', overflowY: 'auto' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '700' }}>Detail de la session</h3>
                  <p style={{ fontSize: '12px', color: '#888', marginTop: '2px', fontFamily: 'monospace' }}>{selectedSession}</p>
                </div>
                <button onClick={() => { setSelectedSession(null); setSessionDetail(null); }}
                  style={{ background: '#f5f5f5', border: 'none', borderRadius: '50%', width: '36px', height: '36px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <X size={18} />
                </button>
              </div>

              <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '11px', padding: '4px 10px', background: '#E8F5E9', borderRadius: '6px', color: '#2E7D32' }}>
                  {sessionDetail.session?.response_format === 'audio' ? 'Format Audio' : 'Format Texte'}
                </span>
                <span style={{ fontSize: '11px', padding: '4px 10px', background: '#f5f5f5', borderRadius: '6px', color: '#666' }}>
                  {new Date(sessionDetail.session?.started_at).toLocaleString('fr-FR')}
                </span>
                <span style={{ fontSize: '11px', padding: '4px 10px', background: sessionDetail.session?.is_completed ? '#E8F5E9' : '#FFF3E0', borderRadius: '6px', color: sessionDetail.session?.is_completed ? '#2E7D32' : '#E65100' }}>
                  {sessionDetail.session?.is_completed ? 'Terminee' : 'Non terminee'}
                </span>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {sessionDetail.responses?.map((r, i) => (
                  <div key={i} style={{ background: '#f8faf8', borderRadius: '12px', padding: '14px 18px', border: '1px solid #eee' }}>
                    <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{r.section} — Q{i + 1}</div>
                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#333', marginBottom: '8px' }}>{r.question_text}</div>
                    <div style={{ fontSize: '14px', color: '#1B5E20', fontWeight: '500' }}>
                      {r.audio_url ? (
                        <audio src={r.audio_url ? `${API_BASE_URL}${r.audio_url}` : ''} controls style={{ height: '32px' }} />
                      ) : (
                        <AnswerValue r={r} />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const SATISFACTION_COLORS = {
  'Très satisfait(e)': { bg: '#E8F5E9', color: '#2E7D32' },
  'Satisfait(e)': { bg: '#F1F8E9', color: '#558B2F' },
  'Peu satisfait(e)': { bg: '#FFF3E0', color: '#E65100' },
  'Pas du tout satisfait(e)': { bg: '#FFEBEE', color: '#C62828' },
  'Sans avis': { bg: '#F5F5F5', color: '#757575' },
};

function SatisfactionBadge({ value }) {
  const c = SATISFACTION_COLORS[value] || { bg: '#E3F2FD', color: '#1565C0' };
  return (
    <span style={{ fontSize: '11px', padding: '3px 10px', borderRadius: '20px', background: c.bg, color: c.color, fontWeight: '600', whiteSpace: 'nowrap' }}>
      {value}
    </span>
  );
}

function AnswerValue({ r }) {
  if (!r.answer_json) {
    return <span>{r.answer_text || '—'}</span>;
  }

  let data;
  try {
    data = typeof r.answer_json === 'string' ? JSON.parse(r.answer_json) : r.answer_json;
  } catch (e) {
    return <span>{r.answer_text || '—'}</span>;
  }

  // Tableau (choix multiple)
  if (Array.isArray(data)) {
    if (data.length === 0) return <span>—</span>;
    return (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
        {data.map((item, i) => (
          <span key={i} style={{ fontSize: '12px', padding: '4px 10px', borderRadius: '20px', background: '#E8F5E9', color: '#2E7D32', fontWeight: '500' }}>{String(item)}</span>
        ))}
      </div>
    );
  }

  if (data && typeof data === 'object') {
    // Classement / priorités
    const list = data.priorities || data.ranking;
    if (Array.isArray(list)) {
      return (
        <ol style={{ margin: 0, paddingLeft: '20px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {list.map((item, i) => (
            <li key={i} style={{ fontSize: '13px', color: '#333' }}>{String(item)}</li>
          ))}
        </ol>
      );
    }

    // Matrice de satisfaction par domaine (domaine -> niveau)
    const entries = Object.entries(data);
    const isSatisfaction = entries.some(([, v]) => SATISFACTION_COLORS[v]);
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', background: 'white', borderRadius: '8px', padding: '10px', border: '1px solid #eee' }}>
        {entries.map(([k, v], i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', paddingBottom: '6px', borderBottom: i < entries.length - 1 ? '1px solid #f5f5f5' : 'none' }}>
            <span style={{ fontSize: '13px', color: '#444', flex: 1 }}>{k}</span>
            {isSatisfaction ? <SatisfactionBadge value={v} /> : <span style={{ fontSize: '13px', color: '#1B5E20', fontWeight: '600' }}>{String(v)}</span>}
          </div>
        ))}
      </div>
    );
  }

  return <span>{String(data)}</span>;
}

export default AdminResponses;
